/**
 * The mid-session substitute (`T48`, blueprint §9.1): the plan is NOT edited —
 * the swap lands on the remaining sets' `exerciseId` and nowhere else. The
 * plan's item keeps its exercise; the engine hears about the change through
 * the recorded sets and the feedback form, and `advance-plan` (`T33`) decides
 * what the NEXT session makes of it.
 *
 * The sheet the choice is made in is T35's `AlternativesSheet`, which renders
 * `ProposedItem` cards — so this file's job is translation: a plan item, and
 * the alternatives `GET /me/plan/items/:id/alternatives` returns for it, as
 * the cards the sheet already knows how to draw. The alternative mapper
 * mirrors the deck's own (`engine-proposer.ts`) because both read the same
 * wire shape: the alternative carries what DIFFERS — id, name, clip, reason —
 * and inherits the rest, targets included, since a substitution keeps the
 * prescription and only moves the exercise.
 */
import { addExclusion, applyPlanEdits, OTHER_EQUIPMENT_REASON, planEditFor, replacementItem, } from "./plan-write";
import { DEFAULT_REST_SECONDS } from "./rest-timer";
/**
 * A plan item as the card the sheet's rows compare against. Targets are
 * nullable on the wire; the card's numbers are not — unspecified reads as a
 * plain default, exactly as the deck falls back.
 *
 * ── THIS IS THE RENDER SHAPE. IT MUST NEVER REACH A WRITE ──────────────────
 * The defaults below exist so a row can PRINT something: a card with no
 * number on it is not a card. They are invention, and invention is fine on a
 * screen and catastrophic in a plan. `3 × 8–12` under a plank is a sentence
 * nobody wrote; `3 × 8–12` stored against a plank is a prescription
 * `advance-plan` will progress. {@link planEditsForSwap} therefore builds its
 * write from `PlanItemView` and never from this — see its note. Three screens
 * call this (`index.tsx`, `session/[id].tsx`, `plan/week.tsx`) and all three
 * only draw with it.
 */
export function itemAsCard(item) {
    return {
        clip: item.exercise.clip,
        // Undefined against a server older than the field, and the card then
        // draws no tip box — which is why this is NOT defaulted to `null`.
        coachingTip: item.exercise.coachingTip,
        // Both undefined against a server older than the field: the picker then
        // compares no level, and the exercise reads as reps and weight.
        difficultyLevel: item.exercise.difficultyLevel,
        equipment: item.exercise.equipment,
        exerciseId: item.exerciseId,
        itemId: item.itemId,
        name: item.exercise.name,
        primaryMuscle: item.exercise.primaryMuscle,
        reasonKey: item.reason?.key,
        reasonParams: item.reason?.params,
        restSeconds: item.restSeconds ?? DEFAULT_REST_SECONDS,
        secondaryMuscles: item.exercise.secondaryMuscles,
        // A plan item is either held or counted, and an older server saying
        // nothing means counted — the same answer as an explicit `null`.
        targetDurationSeconds: item.targetDurationSeconds ?? null,
        // NOT `?? something`: `null` is bodyweight and the card renders it.
        targetLoad: item.targetLoad,
        targetRepsMax: item.targetRepsMax ?? 12,
        targetRepsMin: item.targetRepsMin ?? 8,
        targetSets: item.targetSets ?? 3,
        tracks: item.exercise.tracks,
    };
}
/**
 * An alternative as a card, inheriting from the card it would replace — the
 * same field-by-field reasoning as the deck's `toProposedAlternative`: the
 * muscle is the same by construction (every reason key is `same_muscle_*`)
 * and the equipment differs only when the engine names it in
 * `reasonParams.equipment`, as a localised name with no id.
 *
 * ── FOUR FIELDS ARE DELIBERATELY LEFT UNDEFINED ───────────────────────────
 * `targetLoad`, `targetDurationSeconds`, `coachingTip` and `secondaryMuscles`
 * are NOT inherited. Sets, reps and rest carry over because a substitution
 * keeps the prescription; a LOAD does not — 80 kg was written for the bench
 * press and means nothing under a machine press, which is the same reason
 * `planItemReplaceInput` answers `targetLoad: null`. The hold, the tip and
 * the secondary muscles belong to the exercise itself, and
 * `planItemAlternative` carries none of them. Undefined is this card's word
 * for *"unknown here"*, and the cells are hidden on it rather than drawn
 * wrong.
 */
export function alternativeAsCard(alternative, original) {
    const namedEquipment = alternative.reasonParams.equipment;
    const equipment = alternative.reasonKey === OTHER_EQUIPMENT_REASON &&
        typeof namedEquipment === "string"
        ? { id: null, name: namedEquipment }
        : original.equipment;
    return {
        clip: alternative.clip,
        equipment,
        exerciseId: alternative.exerciseId,
        name: alternative.name,
        primaryMuscle: original.primaryMuscle,
        reasonKey: alternative.reasonKey,
        reasonParams: alternative.reasonParams,
        restSeconds: original.restSeconds,
        targetRepsMax: original.targetRepsMax,
        targetRepsMin: original.targetRepsMin,
        targetSets: original.targetSets,
    };
}
/** The chosen card, kept as what the remaining sets will be recorded under. */
export function asSubstitution(card) {
    return {
        clip: card.clip,
        exerciseId: card.exerciseId,
        name: card.name,
        reasonKey: card.reasonKey,
        reasonParams: card.reasonParams,
    };
}
/** The order the sheet offers them in: least commitment first. */
export const SWAP_SCOPES = ["today", "plan", "never"];
/** Pre-selected. The person is mid-workout; this one needs no thought. */
export const DEFAULT_SWAP_SCOPE = "today";
/**
 * The label key per scope, as a table rather than a `switch`, so a scope added
 * to `SwapScope` without a key stops compiling instead of rendering the
 * generic error sentence a missing key produces (`reference/copy.md`).
 */
export const SWAP_SCOPE_KEY = {
    never: "swap.scope_never",
    plan: "swap.scope_plan",
    today: "swap.scope_today",
};
/**
 * The calls that make the published plan carry the substitute in place of the
 * item that was swapped.
 *
 * Pure, and expressed as *"the session's rows, with one row's exercise
 * changed"* — a `PlanItemView` list diffed by `planEditFor`, the Week
 * screen's own diff, which is the only one in this app that carries a `null`
 * target across as `null`. `replacementItem` builds the standing-in row: the
 * substitute's identity, the ORIGINAL's prescription verbatim, and a pending
 * `itemId`, which the diff reads as *"not one of the rows on file"*. So the
 * swap costs one DELETE, one POST and — unless the swapped row was already
 * last — one PUT that puts the new row back in the old one's slot rather than
 * at the tail.
 *
 * ── WHY NOT `planEdits`, WHICH IS RIGHT NEXT DOOR AND DOES THE SAME SHAPE ──
 * Because it diffs CARDS, and a card's targets are non-nullable: this
 * function used to call it, and it wrote `3 × 8–12 reps` into every timed item
 * anyone swapped. `plan-write.ts`'s header argues the whole case; the short
 * version is that the deck confirms a deck and this confirms a plan, and only
 * one of those two is allowed to invent a number.
 *
 * Nothing that `itemAsCard` produced reaches this path any more, which also
 * settles `D148` by construction rather than by care: the readiness trim
 * lives on a card, and no card is read here.
 */
export function planEditsForSwap(plan, sessionId, itemId, substitute) {
    const session = plan.sessions.find((candidate) => candidate.sessionId === sessionId);
    if (session === undefined) {
        throw new Error(`plan ${plan.planId} has no session ${sessionId}`);
    }
    const next = session.items.map((item) => item.itemId === itemId ? replacementItem(item, substitute) : item);
    return planEditFor(plan.planId, sessionId, session.items, next);
}
/**
 * The scope, applied.
 *
 * `today` writes NOTHING — the swap already landed on the remaining sets
 * before this was called, which is what lets the caller treat every failure
 * here as recoverable: the person keeps lifting the substitute whatever
 * happens on the wire.
 *
 * The plan edit runs first and the exclusion second, so a plan edit that fails
 * leaves the list unwritten rather than excluding an exercise that is still in
 * this week's plan. The reverse order fails the other way, and that failure is
 * the one the person notices on Thursday.
 *
 * `publishPlan` is deliberately NOT called: this edits the plan already in
 * effect, and publishing one that is in effect is at best a no-op.
 */
export async function applySwapScope(fetcher, request) {
    if (request.scope === "today") {
        return;
    }
    await applyPlanEdits(fetcher, planEditsForSwap(request.plan, request.sessionId, request.itemId, request.substitute));
    if (request.scope === "never") {
        await addExclusion(fetcher, request.replacedExerciseId);
    }
}
//# sourceMappingURL=substitution.js.map